// src/components/MenuManagement.js
import React, { useState, useEffect } from "react";
import { Plus, Edit2, Trash2, X } from "lucide-react";
import {
  getMenuItems,
  getCategories,
  createMenuItem,
  updateMenuItem,
  deleteMenuItem,
} from "../api";

// =========================================================
// DEFAULT "NO IMAGE" ILLUSTRATION — plate & spoon, SwiftOrder style
// =========================================================

const PLATE_SPOON_SVG_MARKUP = `
  <div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:#f2ede1;">
    <svg width="56" height="56" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="32" cy="32" r="27" fill="#fbf9f4" stroke="#ddd4c2" stroke-width="2"/>
      <circle cx="32" cy="32" r="17" fill="none" stroke="#ddd4c2" stroke-width="2"/>
      <path d="M17 16 L17 30 C17 32.2 18.8 34 21 34 C23.2 34 25 32.2 25 30 L25 16" stroke="#a49c8d" stroke-width="2" stroke-linecap="round"/>
      <line x1="21" y1="16" x2="21" y2="48" stroke="#a49c8d" stroke-width="2" stroke-linecap="round"/>
      <path d="M45 16 C41 16 39 20 39 25 C39 29 41 31 45 31 L45 48" stroke="#a49c8d" stroke-width="2" stroke-linecap="round" fill="none"/>
    </svg>
  </div>
`;

export default function MenuManagement() {
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    costPrice: "",
    categoryId: "",
    available: true,
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    loadMenuItems();
    loadCategories();
  }, []);

  async function loadMenuItems() {
    try {
      const data = await getMenuItems();
      setMenuItems(data || []);
    } catch (e) {
      console.error("Error loading menu items:", e);
    }
  }

  async function loadCategories() {
    try {
      const data = await getCategories();
      setCategories(data || []);
    } catch (e) {
      console.error("Error loading categories:", e);
    }
  }

  const openAddModal = () => {
    setEditingItem(null);
    setFormData({
      name: "",
      description: "",
      price: "",
      costPrice: "",
      categoryId: "",
      available: true,
    });
    setImageFile(null);
    setImagePreview(null);
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      description: item.description || "",
      price: item.price,
      costPrice: item.costPrice ?? "",
      categoryId: item.category ? item.category.id : "",
      available: item.available,
    });

    // preview from backend image endpoint
    setImageFile(null);
    setImagePreview(
      `http://localhost:8080/api/menu-items/${item.id}/image`
    );

    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this menu item?")) return;
    try {
      const res = await deleteMenuItem(id);
      alert(typeof res === "string" ? res : "Item deleted");
      await loadMenuItems();
    } catch (err) {
      console.error("Error deleting item:", err);
      alert("Failed to delete item (maybe linked to orders)");
    }
  };

  const handleImageFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setImageFile(null);
      setImagePreview(null);
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file)); // local preview only
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const itemData = {
      name: formData.name,
      description: formData.description,
      price: parseFloat(formData.price),
      costPrice: parseFloat(formData.costPrice || 0), // 🔹 send to backend
      categoryId: formData.categoryId || null,
      available: formData.available,
    };

    try {
      if (editingItem) {
        await updateMenuItem(editingItem.id, itemData, imageFile);
      } else {
        await createMenuItem(itemData, imageFile);
      }
      setShowModal(false);
      await loadMenuItems();
    } catch (err) {
      console.error("Error saving item:", err);
      alert("Error saving item");
    }
  };

  const getItemImageSrc = (item) =>
    // use image endpoint; browser will 404 if none, we can handle with onError
    `http://localhost:8080/api/menu-items/${item.id}/image`;

  return (
    <div className="so-menu-mgmt p-8 h-full overflow-auto">

      {/* Scoped SwiftOrder theme — same tokens as the rest of the POS. */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

        .so-menu-mgmt {
          --ink: #1c1a17;
          --paper: #fbf9f4;
          --paper-dim: #f2ede1;
          --chili: #e4572e;
          --chili-dark: #c43f1a;
          --herb: #3f6b4f;
          --herb-dark: #2d4f39;
          --stone: #6b6459;
          --stone-light: #a49c8d;
          --line: #ddd4c2;
          --font-display: 'Bebas Neue', 'Arial Narrow', sans-serif;
          --font-body: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          --font-mono: 'JetBrains Mono', 'Courier New', monospace;

          background: var(--paper-dim);
          background-image: radial-gradient(circle at 1px 1px, var(--line) 1px, transparent 1.6px);
          background-size: 24px 24px;
          font-family: var(--font-body);
          color: var(--ink);
        }

        .so-menu-mgmt * {
          box-sizing: border-box;
        }

        .so-menu-title {
          font-family: var(--font-display);
          font-size: 2rem;
          letter-spacing: 0.01em;
          color: var(--ink);
        }

        .so-menu-add-btn {
          background: var(--chili);
          color: var(--paper);
          border-radius: 8px;
          box-shadow: 0 10px 22px -10px rgba(228, 87, 46, 0.55);
          transition: background 0.15s ease;
        }

        .so-menu-add-btn:hover {
          background: var(--chili-dark);
        }

        .so-menu-card {
          background: #fffdf8;
          border: 1px solid var(--line);
          border-radius: 12px;
          overflow: hidden;
          transition: border-color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;
        }

        .so-menu-card:hover {
          border-color: var(--chili);
          transform: translateY(-2px);
          box-shadow: 0 16px 28px -20px rgba(28, 26, 23, 0.4);
        }

        .so-menu-card-image-wrap {
          background: var(--paper-dim);
        }

        .so-menu-item-name {
          color: var(--ink);
        }

        .so-menu-item-desc {
          color: var(--stone);
        }

        .so-menu-item-price {
          color: var(--herb-dark);
        }

        .so-menu-badge-available {
          background: #eaf3ee;
          color: var(--herb-dark);
        }

        .so-menu-badge-unavailable {
          background: #fdece6;
          color: var(--chili-dark);
        }

        .so-menu-edit-btn {
          background: var(--paper-dim);
          color: var(--ink);
          border-radius: 8px;
          transition: background 0.15s ease;
        }

        .so-menu-edit-btn:hover {
          background: var(--line);
        }

        .so-menu-delete-btn {
          background: #fdece6;
          color: var(--chili-dark);
          border-radius: 8px;
          transition: background 0.15s ease;
        }

        .so-menu-delete-btn:hover {
          background: #f09595;
        }

        .so-menu-empty {
          color: var(--stone-light);
        }

        .so-menu-modal-overlay {
          background: rgba(28, 26, 23, 0.55);
        }

        .so-menu-modal-card {
          background: #fffdf8;
          border-radius: 12px;
          box-shadow: 0 24px 50px -20px rgba(28, 26, 23, 0.45);
        }

        .so-menu-modal-header {
          border-bottom: 1px solid var(--line);
        }

        .so-menu-modal-title {
          font-family: var(--font-display);
          font-size: 1.5rem;
          letter-spacing: 0.01em;
          color: var(--ink);
        }

        .so-menu-modal-close {
          color: var(--stone);
          transition: color 0.15s ease;
        }

        .so-menu-modal-close:hover {
          color: var(--chili);
        }

        .so-menu-label {
          font-size: 0.78rem;
          font-weight: 700;
          font-family: var(--font-mono);
          letter-spacing: 0.03em;
          color: var(--stone);
          display: block;
          margin-bottom: 8px;
        }

        .so-menu-input {
          width: 100%;
          border: 1px solid var(--line);
          border-radius: 8px;
          padding: 9px 12px;
          background: var(--paper);
          color: var(--ink);
          outline: none;
          font-size: 0.93rem;
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
        }

        .so-menu-input:focus {
          border-color: var(--chili);
          box-shadow: 0 0 0 3px rgba(228, 87, 46, 0.15);
        }

        .so-menu-preview-img {
          border: 1px solid var(--line);
        }

        .so-menu-checkbox-label {
          display: flex;
          align-items: center;
          gap: 8px;
          color: var(--ink);
          font-size: 0.92rem;
        }

        .so-menu-checkbox-label input {
          accent-color: var(--chili);
          width: 16px;
          height: 16px;
        }

        .so-menu-cancel-btn {
          border: 1px solid var(--line);
          color: var(--ink);
          background: #fffdf8;
          border-radius: 8px;
          transition: background 0.15s ease;
        }

        .so-menu-cancel-btn:hover {
          background: var(--paper-dim);
        }

        .so-menu-save-btn {
          background: var(--chili);
          color: var(--paper);
          border-radius: 8px;
          box-shadow: 0 10px 22px -10px rgba(228, 87, 46, 0.55);
          transition: background 0.15s ease;
        }

        .so-menu-save-btn:hover {
          background: var(--chili-dark);
        }
      `}</style>

      <div className="flex justify-between items-center mb-6">
        <h2 className="so-menu-title">Menu Items</h2>
        <button
          onClick={openAddModal}
          className="so-menu-add-btn flex items-center gap-2 px-4 py-2"
        >
          <Plus size={20} /> Add Item
        </button>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {menuItems.map((item) => (
          <div
            key={item.id}
            className="so-menu-card"
          >
            <div className="so-menu-card-image-wrap h-48 overflow-hidden">
              <img
                src={getItemImageSrc(item)}
                alt={item.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                  e.currentTarget.parentElement.innerHTML =
                    PLATE_SPOON_SVG_MARKUP;
                }}
              />
            </div>
            <div className="p-4">
              <h3 className="so-menu-item-name text-lg font-semibold mb-1">
                {item.name}
              </h3>
              <p className="so-menu-item-desc text-sm mb-2">
                {item.description}
              </p>
              <div className="flex justify-between items-center mb-3">
                <span className="so-menu-item-price text-xl font-bold">
                  ₹{parseFloat(item.price).toFixed(2)}
                </span>
                <span
                  className={`px-2 py-1 text-xs rounded ${
                    item.available
                      ? "so-menu-badge-available"
                      : "so-menu-badge-unavailable"
                  }`}
                >
                  {item.available ? "Available" : "Unavailable"}
                </span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(item)}
                  className="so-menu-edit-btn flex-1 flex items-center justify-center gap-1 px-3 py-2"
                >
                  <Edit2 size={16} /> Edit
                </button>
                <button
                  onClick={() => handleDelete(item.id)}
                  className="so-menu-delete-btn flex-1 flex items-center justify-center gap-1 px-3 py-2"
                >
                  <Trash2 size={16} /> Delete
                </button>
              </div>
            </div>
          </div>
        ))}
        {menuItems.length === 0 && (
          <p className="so-menu-empty">No menu items yet.</p>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="so-menu-modal-overlay fixed inset-0 flex items-center justify-center z-50 p-4">
          <div className="so-menu-modal-card w-full max-w-md">
            <div className="so-menu-modal-header flex justify-between items-center p-6">
              <h3 className="so-menu-modal-title">
                {editingItem ? "Edit Item" : "Add New Item"}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="so-menu-modal-close"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              {/* Name */}
              <div className="mb-4">
                <label className="so-menu-label">
                  Item Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="so-menu-input"
                  required
                />
              </div>

              {/* Description */}
              <div className="mb-4">
                <label className="so-menu-label">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      description: e.target.value,
                    })
                  }
                  className="so-menu-input"
                />
              </div>

              {/* Price */}
              <div className="mb-4">
                <label className="so-menu-label">
                  Price (₹)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) =>
                    setFormData({ ...formData, price: e.target.value })
                  }
                  className="so-menu-input"
                  required
                />
              </div>
              <div className="mb-4">
  <label className="so-menu-label">
    Cost Price (₹)
  </label>
  <input
    type="number"
    step="0.01"
    value={formData.costPrice ?? ""}   // ✅ never undefined
    onChange={(e) =>
      setFormData({ ...formData, costPrice: e.target.value })
    }
    className="so-menu-input"
    required
  />
</div>



              {/* Category */}
              <div className="mb-4">
                <label className="so-menu-label">
                  Category
                </label>
                <select
                  value={formData.categoryId}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      categoryId: e.target.value,
                    })
                  }
                  className="so-menu-input"
                >
                  <option value="">Select Category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Image file */}
              <div className="mb-4">
                <label className="so-menu-label">
                  Image (from your computer)
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageFileChange}
                  className="w-full text-sm"
                />
                {imagePreview && (
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="so-menu-preview-img mt-2 h-24 rounded object-cover"
                  />
                )}
              </div>

              {/* Available */}
              <div className="mb-6">
                <label className="so-menu-checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.available}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        available: e.target.checked,
                      })
                    }
                  />
                  Available
                </label>
              </div>

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="so-menu-cancel-btn flex-1 px-4 py-2"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="so-menu-save-btn flex-1 px-4 py-2"
                >
                  {editingItem ? "Update" : "Add"} Item
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}














// // src/components/MenuManagement.js
// import React, { useState, useEffect } from "react";
// import { Plus, Edit2, Trash2, X } from "lucide-react";
// import {
//   getMenuItems,
//   getCategories,
//   createMenuItem,
//   updateMenuItem,
//   deleteMenuItem,
// } from "../api";

// export default function MenuManagement() {
//   const [menuItems, setMenuItems] = useState([]);
//   const [categories, setCategories] = useState([]);
//   const [showModal, setShowModal] = useState(false);
//   const [editingItem, setEditingItem] = useState(null);
//   const [formData, setFormData] = useState({
//     name: "",
//     description: "",
//     price: "",
//     costPrice: "",
//     categoryId: "",
//     available: true,
//   });
//   const [imageFile, setImageFile] = useState(null);
//   const [imagePreview, setImagePreview] = useState(null);

//   useEffect(() => {
//     loadMenuItems();
//     loadCategories();
//   }, []);

//   async function loadMenuItems() {
//     try {
//       const data = await getMenuItems();
//       setMenuItems(data || []);
//     } catch (e) {
//       console.error("Error loading menu items:", e);
//     }
//   }

//   async function loadCategories() {
//     try {
//       const data = await getCategories();
//       setCategories(data || []);
//     } catch (e) {
//       console.error("Error loading categories:", e);
//     }
//   }

//   const openAddModal = () => {
//     setEditingItem(null);
//     setFormData({
//       name: "",
//       description: "",
//       price: "",
//       costPrice: "",      
//       categoryId: "",
//       available: true,
//     });
//     setImageFile(null);
//     setImagePreview(null);
//     setShowModal(true);
//   };

//   const handleEdit = (item) => {
//     setEditingItem(item);
//     setFormData({
//       name: item.name,
//       description: item.description || "",
//       price: item.price,
//       costPrice: item.costPrice ?? "",
//       categoryId: item.category ? item.category.id : "",
//       available: item.available,
//     });

//     // preview from backend image endpoint
//     setImageFile(null);
//     setImagePreview(
//       `http://localhost:8080/api/menu-items/${item.id}/image`
//     );

//     setShowModal(true);
//   };

//   const handleDelete = async (id) => {
//     if (!window.confirm("Delete this menu item?")) return;
//     try {
//       const res = await deleteMenuItem(id);
//       alert(typeof res === "string" ? res : "Item deleted");
//       await loadMenuItems();
//     } catch (err) {
//       console.error("Error deleting item:", err);
//       alert("Failed to delete item (maybe linked to orders)");
//     }
//   };

//   const handleImageFileChange = (e) => {
//     const file = e.target.files?.[0];
//     if (!file) {
//       setImageFile(null);
//       setImagePreview(null);
//       return;
//     }
//     setImageFile(file);
//     setImagePreview(URL.createObjectURL(file)); // local preview only
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     const itemData = {
//       name: formData.name,
//       description: formData.description,
//       price: parseFloat(formData.price),
//       costPrice: parseFloat(formData.costPrice || 0), // 🔹 send to backend
//       categoryId: formData.categoryId || null,
//       available: formData.available,
//     };

//     try {
//       if (editingItem) {
//         await updateMenuItem(editingItem.id, itemData, imageFile);
//       } else {
//         await createMenuItem(itemData, imageFile);
//       }
//       setShowModal(false);
//       await loadMenuItems();
//     } catch (err) {
//       console.error("Error saving item:", err);
//       alert("Error saving item");
//     }
//   };

//   const getItemImageSrc = (item) =>
//     // use image endpoint; browser will 404 if none, we can handle with onError
//     `http://localhost:8080/api/menu-items/${item.id}/image`;

//   return (
//     <div className="p-8 h-full overflow-auto">
//       <div className="flex justify-between items-center mb-6">
//         <h2 className="text-3xl font-bold text-slate-800">Menu Items</h2>
//         <button
//           onClick={openAddModal}
//           className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
//         >
//           <Plus size={20} /> Add Item
//         </button>
//       </div>

//       {/* Cards */}
//       <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
//         {menuItems.map((item) => (
//           <div
//             key={item.id}
//             className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow"
//           >
//             <div className="h-48 bg-slate-200 overflow-hidden">
//               <img
//                 src={getItemImageSrc(item)}
//                 alt={item.name}
//                 className="w-full h-full object-cover"
//                 onError={(e) => {
//                   e.currentTarget.style.display = "none";
//                   e.currentTarget.parentElement.innerHTML =
//                     '<div class="w-full h-full flex items-center justify-center text-slate-400">No Image</div>';
//                 }}
//               />
//             </div>
//             <div className="p-4">
//               <h3 className="text-lg font-semibold text-slate-800 mb-1">
//                 {item.name}
//               </h3>
//               <p className="text-sm text-slate-600 mb-2">
//                 {item.description}
//               </p>
//               <div className="flex justify-between items-center mb-3">
//                 <span className="text-xl font-bold text-green-600">
//                   ₹{parseFloat(item.price).toFixed(2)}
//                 </span>
//                 <span
//                   className={`px-2 py-1 text-xs rounded ${
//                     item.available
//                       ? "bg-green-100 text-green-800"
//                       : "bg-red-100 text-red-800"
//                   }`}
//                 >
//                   {item.available ? "Available" : "Unavailable"}
//                 </span>
//               </div>
//               <div className="flex gap-2">
//                 <button
//                   onClick={() => handleEdit(item)}
//                   className="flex-1 flex items-center justify-center gap-1 bg-slate-100 text-slate-700 px-3 py-2 rounded hover:bg-slate-200 transition-colors"
//                 >
//                   <Edit2 size={16} /> Edit
//                 </button>
//                 <button
//                   onClick={() => handleDelete(item.id)}
//                   className="flex-1 flex items-center justify-center gap-1 bg-red-100 text-red-700 px-3 py-2 rounded hover:bg-red-200 transition-colors"
//                 >
//                   <Trash2 size={16} /> Delete
//                 </button>
//               </div>
//             </div>
//           </div>
//         ))}
//         {menuItems.length === 0 && (
//           <p className="text-slate-500">No menu items yet.</p>
//         )}
//       </div>

//       {/* Modal */}
//       {showModal && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//           <div className="bg-white rounded-lg w-full max-w-md">
//             <div className="flex justify-between items-center p-6 border-b">
//               <h3 className="text-xl font-bold text-slate-800">
//                 {editingItem ? "Edit Item" : "Add New Item"}
//               </h3>
//               <button
//                 onClick={() => setShowModal(false)}
//                 className="text-slate-400 hover:text-slate-600"
//               >
//                 <X size={24} />
//               </button>
//             </div>

//             <form onSubmit={handleSubmit} className="p-6">
//               {/* Name */}
//               <div className="mb-4">
//                 <label className="block text-sm font-medium text-slate-700 mb-2">
//                   Item Name
//                 </label>
//                 <input
//                   type="text"
//                   value={formData.name}
//                   onChange={(e) =>
//                     setFormData({ ...formData, name: e.target.value })
//                   }
//                   className="w-full px-3 py-2 border rounded"
//                   required
//                 />
//               </div>

//               {/* Description */}
//               <div className="mb-4">
//                 <label className="block text-sm font-medium text-slate-700 mb-2">
//                   Description
//                 </label>
//                 <textarea
//                   value={formData.description}
//                   onChange={(e) =>
//                     setFormData({
//                       ...formData,
//                       description: e.target.value,
//                     })
//                   }
//                   className="w-full px-3 py-2 border rounded"
//                 />
//               </div>

//               {/* Price */}
//               <div className="mb-4">
//                 <label className="block text-sm font-medium text-slate-700 mb-2">
//                   Price (₹)
//                 </label>
//                 <input
//                   type="number"
//                   step="0.01"
//                   value={formData.price}
//                   onChange={(e) =>
//                     setFormData({ ...formData, price: e.target.value })
//                   }
//                   className="w-full px-3 py-2 border rounded"
//                   required
//                 />
//               </div>
//               <div className="mb-4">
//   <label className="block text-sm font-medium text-slate-700 mb-2">
//     Cost Price (₹)
//   </label>
//   <input
//     type="number"
//     step="0.01"
//     value={formData.costPrice ?? ""}   // ✅ never undefined
//     onChange={(e) =>
//       setFormData({ ...formData, costPrice: e.target.value })
//     }
//     className="w-full px-3 py-2 border rounded"
//     required
//   />
// </div>



//               {/* Category */}
//               <div className="mb-4">
//                 <label className="block text-sm font-medium text-slate-700 mb-2">
//                   Category
//                 </label>
//                 <select
//                   value={formData.categoryId}
//                   onChange={(e) =>
//                     setFormData({
//                       ...formData,
//                       categoryId: e.target.value,
//                     })
//                   }
//                   className="w-full px-3 py-2 border rounded"
//                 >
//                   <option value="">Select Category</option>
//                   {categories.map((cat) => (
//                     <option key={cat.id} value={cat.id}>
//                       {cat.name}
//                     </option>
//                   ))}
//                 </select>
//               </div>

//               {/* Image file */}
//               <div className="mb-4">
//                 <label className="block text-sm font-medium text-slate-700 mb-2">
//                   Image (from your computer)
//                 </label>
//                 <input
//                   type="file"
//                   accept="image/*"
//                   onChange={handleImageFileChange}
//                   className="w-full text-sm"
//                 />
//                 {imagePreview && (
//                   <img
//                     src={imagePreview}
//                     alt="Preview"
//                     className="mt-2 h-24 rounded border object-cover"
//                   />
//                 )}
//               </div>

//               {/* Available */}
//               <div className="mb-6">
//                 <label className="flex items-center gap-2">
//                   <input
//                     type="checkbox"
//                     checked={formData.available}
//                     onChange={(e) =>
//                       setFormData({
//                         ...formData,
//                         available: e.target.checked,
//                       })
//                     }
//                   />
//                   Available
//                 </label>
//               </div>

//               {/* Buttons */}
//               <div className="flex gap-3">
//                 <button
//                   type="button"
//                   onClick={() => setShowModal(false)}
//                   className="flex-1 px-4 py-2 border rounded"
//                 >
//                   Cancel
//                 </button>
//                 <button
//                   type="submit"
//                   className="flex-1 px-4 py-2 bg-blue-600 text-white rounded"
//                 >
//                   {editingItem ? "Update" : "Add"} Item
//                 </button>
//               </div>
//             </form>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }
